import { 
    Users, 
    DollarSign, 
    UserPlus, 
    Calculator,
    TrendingUp,
    FileText,
    Calendar,
    Award,
    Bell,
    User
} from 'lucide-react';
  
interface StatsCardProps {
    title: string;
    value: string;
    subtitle: string;
    icon: React.ReactNode;
    color: string;
    trend?: string;
}
  
interface QuickActionProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
    onClick: () => void;
}

export default function HomeDashboard() {
    // Sample data - ganti dengan data real dari API
  const stats = [
    {
      title: "Total Karyawan",
      value: "248",
      subtitle: "Karyawan Aktif",
      icon: <Users className="w-6 h-6" />,
      color: "bg-blue-500",
      trend: "+12 bulan ini"
    },
    {
      title: "Gaji Bulan Ini",
      value: "Rp 1.2M",
      subtitle: "Total Penggajian",
      icon: <DollarSign className="w-6 h-6" />,
      color: "bg-green-500",
      trend: "Februari 2024"
    },
    {
      title: "Karyawan Baru",
      value: "8",
      subtitle: "Bulan Ini",
      icon: <UserPlus className="w-6 h-6" />,
      color: "bg-purple-500",
      trend: "+3 dari bulan lalu"
    },
    {
      title: "Pending Gaji",
      value: "12",
      subtitle: "Perlu Diproses",
      icon: <Calculator className="w-6 h-6" />,
      color: "bg-orange-500",
      trend: "Deadline: 28 Feb"
    }
  ];

  const quickActions = [
    {
      title: "Tambah Karyawan Baru",
      description: "Daftarkan karyawan baru ke sistem",
      icon: <UserPlus className="w-8 h-8" />,
      color: "from-blue-500 to-blue-600",
      onClick: () => console.log("Navigate to add employee")
    },
    {
      title: "Proses Penggajian",
      description: "Hitung dan proses gaji karyawan",
      icon: <Calculator className="w-8 h-8" />,
      color: "from-green-500 to-green-600", 
      onClick: () => console.log("Navigate to payroll")
    },
    {
      title: "Laporan Bulanan",
      description: "Lihat laporan karyawan dan gaji",
      icon: <FileText className="w-8 h-8" />,
      color: "from-purple-500 to-purple-600",
      onClick: () => console.log("Navigate to reports")
    },
    {
      title: "Manajemen Absensi",
      description: "Kelola data kehadiran karyawan",
      icon: <Calendar className="w-8 h-8" />,
      color: "from-orange-500 to-orange-600",
      onClick: () => console.log("Navigate to attendance")
    }
  ];

  const recentActivities = [
    { action: "Karyawan baru ditambahkan", name: "Ahmad Rizki", time: "2 jam lalu", type: "add" },
    { action: "Gaji diproses untuk", name: "Siti Nurhaliza", time: "4 jam lalu", type: "salary" },
    { action: "Data karyawan diupdate", name: "Budi Santoso", time: "6 jam lalu", type: "update" },
    { action: "Absensi dicatat untuk", name: "Maya Sari", time: "1 hari lalu", type: "attendance" },
    { action: "Laporan bulanan dibuat", name: "Sistem", time: "2 hari lalu", type: "report" }
  ];

  const StatsCard: React.FC<StatsCardProps> = ({ title, value, subtitle, icon, color, trend }) => (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${color} text-white`}>
          {icon}
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-800">{value}</div>
          <div className="text-sm text-gray-500">{subtitle}</div>
        </div>
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-1">{title}</h3>
      {trend && (
        <p className="text-xs text-gray-500">{trend}</p>
      )}
    </div>
  );

  const QuickActionCard: React.FC<QuickActionProps> = ({ title, description, icon, color, onClick }) => (
    <div 
      className={`bg-gradient-to-r ${color} rounded-xl p-6 text-white cursor-pointer hover:shadow-xl transform hover:scale-105 transition-all duration-300`}
      onClick={onClick}
    >
      <div className="flex items-center mb-4">
        {icon}
        <div className="ml-4">
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm text-white/80">{description}</p>
        </div>
      </div>
      <div className="flex justify-end">
        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
          <TrendingUp className="w-4 h-4" />
        </div>
      </div>
    </div>
  );

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'add': return <UserPlus className="w-4 h-4 text-green-500" />;
      case 'salary': return <DollarSign className="w-4 h-4 text-blue-500" />;
      case 'update': return <User className="w-4 h-4 text-orange-500" />;
      case 'attendance': return <Calendar className="w-4 h-4 text-purple-500" />;
      case 'report': return <FileText className="w-4 h-4 text-gray-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <StatsCard key={index} {...stat} />
          ))}
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">Aksi Cepat</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickActions.map((action, index) => (
              <QuickActionCard key={index} {...action} />
            ))}
          </div>
        </div>

        {/* Recent Activities */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
            <Award className="w-6 h-6 mr-2 text-blue-600" />
            Aktivitas Terbaru
          </h3>
          <div className="grid grid-cols-1 gap-3">
            {recentActivities.map((activity, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-4 hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-center space-x-4">
                  <div className="flex-shrink-0 p-3 bg-gray-50 rounded-xl">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {activity.action}
                          {activity.name !== "Sistem" && (
                            <span className="text-blue-600 font-semibold"> {activity.name}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <button className="text-gray-400 hover:text-gray-600 p-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">Ringkasan Hari Ini</h3>
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-lg">Kehadiran Hari Ini</h4>
                    <p className="text-sm text-gray-600">234 dari 248 karyawan hadir</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">94%</div>
                    <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                      <div className="bg-blue-500 h-2 rounded-full" style={{width: '94%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-lg">Gaji Diproses</h4>
                    <p className="text-sm text-gray-600">236 dari 248 karyawan</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">95%</div>
                    <div className="w-24 bg-gray-200 rounded-full h-2 mt-1">
                      <div className="bg-green-500 h-2 rounded-full" style={{width: '95%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <FileText className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 text-lg">Pending Review</h4>
                    <p className="text-sm text-gray-600">Data memerlukan verifikasi</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-2xl font-bold text-orange-600">12</div>
                    <button className="text-sm text-orange-600 font-medium hover:text-orange-700 mt-1">
                      Lihat Detail →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </div>
    </>
  )
}